import { useState, useEffect, useCallback } from 'react';
import { Trash2, Loader2, Save, RefreshCw, Globe, BarChart3, Package, ChevronLeft } from 'lucide-react';
import ModulLayout, { PANEL_OD } from '../../components/layout/ModulLayout';
import DodavateleListPanel, { ZPUSOB_PLATBY_LABEL, PRICHOD_FAKTURY, POHLEDY } from '../../components/dodavatele/DodavateleListPanel';
import DodavatelStatistiky from '../../components/dodavatele/DodavatelStatistiky';
import DodavatelHero from '../../components/dodavatele/DodavatelHero';
import ZpetTlacitko from '../../components/ui/ZpetTlacitko';

// Karta dodavatele patří do Financí — statistiky se počítají z dokladů
// (obrat, podíl na nákupech), párování faktur i platby jsou tady. Špižírna
// a Flotila si dodavatele jen vybírají a proklikávají se sem.
const SECTION_COLOR = '#3B82F6';

// Barvy štítků dodavatelů — musí sedět s DodavateleListPanel (stejné kódy kategorií)
const STITEK_BARVY = {
    suroviny: '#10B981', spotrebka: '#EAB308', obaly: '#06B6D4', palivo: '#F97316', sluzby: '#8B5CF6',
    it: '#3B82F6', marketing: '#EC4899', pojistovna: '#14B8A6', energie: '#F59E0B', jine: '#94A3B8',
};

// Sekce formuláře — stejný blok jako detail receptury, ať formuláře napříč
// moduly vypadají a chovají se stejně.
const DodSekce = ({ nazev, doplnek, children, isDark, secondaryColor }) => (
    <div className="rounded-xl" style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
    }}>
        <div className="flex items-center justify-between px-4 py-2.5"
            style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>{nazev}</span>
            {doplnek}
        </div>
        <div className="p-4">{children}</div>
    </div>
);

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const api = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

export default function DodavateleTab({ cardStyle, isDark, initialDodavatelId = null, onNavigate, rezervaDole = null }) {
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    // Široký displej = statistiky jako stálý třetí sloupec; pod 1280 px se
    // schovají do hero pásu v hlavičce detailu.
    const [isWide, setIsWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= PANEL_OD);
    useEffect(() => {
        const onResize = () => { setIsMobile(window.innerWidth < 768); setIsWide(window.innerWidth >= PANEL_OD); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted, #94a3b8)';
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
        width: '100%', outline: 'none', colorScheme: isDark ? 'dark' : 'light',
    };
    const selectStyle = { ...inputStyle, cursor: 'pointer' };
    const optionStyle = { background: isDark ? '#1e1e2e' : '#fff', color: isDark ? '#e2e8f0' : '#1e293b' };
    const btnPrimary = {
        background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 6,
        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
    };
    // „Co od něj bereme" vede do Špižírny se zapnutým filtrem na dodavatele.
    const otevritSortiment = (typ) => onNavigate?.(typ === 'MAT' ? 'spizirna' : 'balirna', {
        dodavatelId: dodDetail,
        polozka: { typ, id: null },
    });

    /* Třetí parametr je nápověda pod polem — u výběrů, kde samotný popisek
       nestačí („sken“ a „podatelna“ jsou jinak dvě slova bez rozdílu). */
    const renderFormField = (label, children, napoveda = null) => (
        <div>
            <label className="block text-xs font-medium mb-1" style={{ color: secondaryColor }}>{label}</label>
            {children}
            {napoveda && <p className="text-[10.5px] mt-1" style={{ color: mutedColor }}>{napoveda}</p>}
        </div>
    );
    const btnSecondary = {
        background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface-base)',
        color: secondaryColor, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    };

    const [dodavatele, setDodavatele] = useState([]);
    const [dodSearch, setDodSearch] = useState('');
    /* Pohled = předem daná sada (aktivní, chodí mailem, bez IČO…), filtr =
       zúžení té sady (kategorie, platba, jak chodí faktura). Dřív tu byl jen
       přepínač aktivní/neaktivní a jeden štítek — sadu ani zúžení to
       neumělo pojmenovat a počty nebyly nikde vidět. */
    const [dodPohled, setDodPohled] = useState('aktivni');
    const [dodFiltr, setDodFiltr] = useState({ kategorie: '', platba: '', faktura: '' });
    const [dodLoading, setDodLoading] = useState(false);
    const [dodSaving, setDodSaving] = useState(false);
    const [dodDetail, setDodDetail] = useState(null);
    const [dodDetailData, setDodDetailData] = useState(null);
    const [dodEditData, setDodEditData] = useState(null);
    const [dodDetailTab, setDodDetailTab] = useState('info');
    const [dodError, setDodError] = useState('');
    const [dodKategorie, setDodKategorie] = useState([]);
    const [dodStats, setDodStats] = useState(null);
    const [dodStatsLoading, setDodStatsLoading] = useState(false);
    const [aresRefreshing, setAresRefreshing] = useState(false);
    // Sortiment (co od něj bereme) se tahá až při otevření záložky — je to
    // dotaz přes tři ceníky a na kartě ho člověk většinou nepotřebuje.
    const [dodSortiment, setDodSortiment] = useState(null);
    const [dodSortimentLoading, setDodSortimentLoading] = useState(false);

    const loadDodavatele = useCallback(async () => {
        setDodLoading(true);
        try { setDodavatele(await api('/api/dodavatele?dny=1&obrat=1&obaly=1')); } catch {}
        setDodLoading(false);
    }, []);

    useEffect(() => { loadDodavatele(); }, []);
    // Číselník kategorií pro stromovou navigaci (stejný jako v modulu Doklady)
    useEffect(() => { api('/api/dodavatele/kategorie').then(setDodKategorie).catch(() => {}); }, []);

    // Statistiky se dotahují zvlášť (doklady + ceníky) — až když je karta otevřená
    const [dodStatsError, setDodStatsError] = useState('');
    const loadDodStats = useCallback(async (id) => {
        setDodStatsLoading(true); setDodStats(null); setDodStatsError('');
        try {
            setDodStats(await api(`/api/dodavatele/${id}/statistiky`));
        } catch (e) {
            // Tichý catch tu dřív schoval i chybu serveru — karta pak jen prázdně
            // svítila a nebylo poznat, že se dotaz vůbec nepovedl.
            setDodStatsError(e?.error || e?.message || 'Statistiky se nepodařilo načíst');
        }
        setDodStatsLoading(false);
    }, []);

    useEffect(() => {
        if (dodDetailTab !== 'sortiment' || !dodDetail || dodDetail === 'new') return;
        let zruseno = false;
        setDodSortimentLoading(true);
        api(`/api/dodavatele/${dodDetail}/sortiment`)
            .then(d => { if (!zruseno) setDodSortiment(d); })
            .catch(() => { if (!zruseno) setDodSortiment(null); })
            .finally(() => { if (!zruseno) setDodSortimentLoading(false); });
        return () => { zruseno = true; };
    }, [dodDetailTab, dodDetail]);

    /**
     * Přetáhne aktuální údaje z ARES podle IČO a přepíše jimi kartu.
     * IČO je jedinečný identifikátor firmy, takže je to autoritativní zdroj —
     * kontakt, poznámky ani pokyny pro AI scan se nepřepisují (ARES je nezná).
     */
    const aresRefresh = async () => {
        if (!dodDetail || dodDetail === 'new') return;
        setAresRefreshing(true); setDodError('');
        try {
            const res = await api(`/api/dodavatele/${dodDetail}/ares-refresh`, 'POST');
            const zmeny = Object.keys(res.zmeny || {});
            const freshList = await api('/api/dodavatele?dny=1&obrat=1&obaly=1');
            setDodavatele(freshList);
            const updated = freshList.find(x => x.id === dodDetail);
            if (updated) {
                setDodDetailData(updated);
                setDodEditData(prev => ({
                    ...prev,
                    nazev: updated.nazev || '', ico: updated.ico || '', adresa: updated.adresa || '',
                }));
            }
            if (dodStats) loadDodStats(dodDetail);
            alert(res.beze_zmeny
                ? 'ARES nemá nic nového — údaje na kartě odpovídají registru.'
                : `Aktualizováno z ARES: ${zmeny.join(', ')}`);
        } catch (e) {
            setDodError(e.error || e.message || 'ARES aktualizace se nezdařila');
        }
        setAresRefreshing(false);
    };

    const loadDodDetail = async (id) => {
        setDodDetail(id);
        setDodDetailTab('info');
        setDodSortiment(null);
        setDodError('');
        setDodStats(null);
        if (id && id !== 'new') loadDodStats(id);
        try {
            const d = dodavatele.find(x => x.id === id);
            if (d) {
                setDodDetailData(d);
                const dny = [0,1,2,3,4,5,6].map(den => {
                    const existing = d.dny?.find(x => x.den === den);
                    return { den, objednavka: existing?.objednavka || false, deadline: existing?.deadline?.slice(0,5) || '', zavoz: existing?.zavoz || false };
                });
                setDodEditData({
                    nazev: d.nazev || '', ico: d.ico || '', kontaktni_osoba: d.kontaktni_osoba || '',
                    telefon: d.telefon || '', email: d.email || '', adresa: d.adresa || '',
                    dodaci_lhuta_dni: d.dodaci_lhuta_dni ?? '', zpusob_objednavky: d.zpusob_objednavky || '',
                    zpusob_platby: d.zpusob_platby || '', zpusob_prichodu_faktury: d.zpusob_prichodu_faktury || '',
                    poznamka: d.poznamka || '', scan_pokyny: d.scan_pokyny || '', scan_jen_souhrn: !!d.scan_jen_souhrn, aktivni: d.aktivni ?? true, _dny: dny,
                    dic: d.dic || '', je_zahranicni: d.je_zahranicni ?? false,
                    _kategorie: (d.kategorie || []).map(k => k.kod),
                });
            }
        } catch {}
    };

    const saveDod = async () => {
        setDodSaving(true); setDodError('');
        try {
            const payload = { ...dodEditData };
            const dny = payload._dny;
            delete payload._dny;
            // Štítky umí uložit stejný request jako zbytek karty — jeden endpoint,
            // jeden zápis. Dřív to byly tři volání přes dvě různá API.
            payload.kategorie = payload._kategorie || [];
            delete payload._kategorie;
            let targetId;
            if (dodDetail === 'new') {
                const saved = await api('/api/dodavatele', 'POST', payload);
                targetId = saved.id;
            } else {
                targetId = dodDetail;
                await api(`/api/dodavatele/${targetId}`, 'PUT', payload);
            }
            if (dny) await api(`/api/dodavatele/${targetId}/dny`, 'PUT', { dny });
            const freshList = await api('/api/dodavatele?dny=1&obrat=1&obaly=1');
            setDodavatele(freshList);
            const updated = freshList.find(x => x.id === targetId);
            if (updated) {
                setDodDetail(targetId);
                setDodDetailData(updated);
                const refreshedDny = [0,1,2,3,4,5,6].map(den => {
                    const existing = updated.dny?.find(x => x.den === den);
                    return { den, objednavka: existing?.objednavka || false, deadline: existing?.deadline?.slice(0,5) || '', zavoz: existing?.zavoz || false };
                });
                setDodEditData(prev => ({ ...prev, _dny: refreshedDny }));
            }
        } catch (e) { setDodError(e.message || e.error || 'Chyba při ukládání'); }
        setDodSaving(false);
    };

    const delDod = async (id) => {
        if (!confirm('Smazat dodavatele?')) return;
        try {
            await api(`/api/dodavatele/${id}`, 'DELETE');
            setDodDetail(null); setDodDetailData(null); setDodEditData(null);
            loadDodavatele();
        } catch (e) { alert(e.error || 'Nelze smazat'); }
    };

    // Proklik zvenčí (Špižírna, Doklady) — otevřít rovnou danou kartu.
    useEffect(() => {
        if (initialDodavatelId && dodavatele.length > 0) loadDodDetail(initialDodavatelId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDodavatelId, dodavatele.length]);

    /* Seznam pro šipky ↑↓ — musí být PŘESNĚ ten, který je vidět: stejný
       pohled, stejné zúžení, stejné řazení. Dřív se tu opisovala jen část
       podmínky a řadilo se podle id, takže šipka uměla skočit na dodavatele,
       který v seznamu vůbec nebyl. */
    const filteredDod = (() => {
        const pohled = POHLEDY.find(p => p.id === dodPohled) || POHLEDY[0];
        const q = dodSearch.trim().toLowerCase();
        return dodavatele
            .filter(d => {
                if (!pohled.test(d)) return false;
                if (dodFiltr.kategorie && !(d.kategorie || []).some(k => k.kod === dodFiltr.kategorie)) return false;
                if (dodFiltr.platba && d.zpusob_platby !== dodFiltr.platba) return false;
                if (dodFiltr.faktura && d.zpusob_prichodu_faktury !== dodFiltr.faktura) return false;
                if (!q) return true;
                return [d.nazev, d.ico, d.kontaktni_osoba, d.email].some(v => (v || '').toLowerCase().includes(q));
            })
            .sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
    })();

    // ─── Keyboard navigace šipkami ↑↓ v seznamu ────────────
    useEffect(() => {
        const handler = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            if (!dodDetail || dodDetail === 'new') return;
            e.preventDefault();

            const dir = e.key === 'ArrowUp' ? -1 : 1;
            const idx = filteredDod.findIndex(d => d.id === dodDetail);
            const next = idx + dir;
            if (next < 0 || next >= filteredDod.length) return;

            const nextId = filteredDod[next].id;
            loadDodDetail(nextId);
            setTimeout(() => {
                document.querySelector(`[data-list-id="${nextId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }, 50);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [dodDetail, filteredDod]);

    return (
        /* Třísloupcový standard (`standard-rozvrzeni.md`):
             seznam dodavatelů | karta dodavatele | statistiky
           Dřív to byl vlastní `flex` s pevnou výškou `calc(100vh − 200px)`:
           seznam se nikdy neroztáhl na přehled, a když nebyl nikdo vybraný,
           svítila vedle něj poloprázdná plocha s „Vyberte dodavatele ze
           seznamu“ — přesně to, co norma zakazuje. */
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }} data-ladit="dodavatele">
            <ModulLayout
                isMobile={isMobile}
                vybrano={!!dodDetail}
                mobilRezim
                mobilRezervaDole={rezervaDole}
                mobilPanel={!!dodDetail && dodDetail !== 'new'}
                mobilPanelTitulek="Statistiky"
                mobilPanelIkona={BarChart3}
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={({ siroky }) => (
                    <DodavateleListPanel
                        siroky={siroky} kompaktni={isMobile} isDark={isDark}
                        mutedColor={mutedColor} secondaryColor={secondaryColor}
                        inputStyle={inputStyle}
                        search={dodSearch} setSearch={setDodSearch}
                        pohled={dodPohled} setPohled={setDodPohled}
                        filtrHodnoty={dodFiltr} setFiltrHodnoty={setDodFiltr}
                        kategorie={dodKategorie} dodavatele={dodavatele} loading={dodLoading}
                        activeId={dodDetail}
                        rezervaDole={isMobile ? rezervaDole : null}
                        onSelect={loadDodDetail}
                        onNew={() => {
                            setDodDetail('new'); setDodDetailData(null); setDodDetailTab('info'); setDodError(''); setDodStats(null);
                            setDodEditData({
                                nazev: '', ico: '', kontaktni_osoba: '', telefon: '', email: '', adresa: '',
                                dodaci_lhuta_dni: '', zpusob_objednavky: '', zpusob_platby: '', zpusob_prichodu_faktury: '',
                                poznamka: '', scan_pokyny: '', scan_jen_souhrn: false, aktivni: true,
                                dic: '', je_zahranicni: false, _kategorie: [],
                                _dny: [0,1,2,3,4,5,6].map(den => ({ den, objednavka: false, deadline: '', zavoz: false })),
                            });
                        }}
                    />
                )}
                detail={() => (
                        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
                            style={isMobile ? null : cardStyle}>
                            {/* Header — název, identifikátor a štítky přímo k editaci */}
                            <div className={isMobile ? 'px-3 pt-2 pb-3' : 'p-4'} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                {/* Na telefonu je detail jediné, co je vidět — cesta zpět
                                    musí být tlačítko na prst (norma 1b). Na širokém
                                    rozvržení seznam nikam nezmizel, tam se nevychází. */}
                                {isMobile && (
                                    <ZpetTlacitko onClick={() => { setDodDetail(null); setDodDetailData(null); setDodEditData(null); }} popis="Zpět na seznam dodavatelů" />
                                )}
                                {/* Na mobilu jméno na vlastní řádek: vedle tří ovladačů se
                                    z něj vešly dvě slabiky („Albert Česk“). */}
                                <div className={isMobile ? 'flex flex-col gap-2' : 'flex items-start justify-between gap-3'}>
                                    <div className="min-w-0 flex-1">
                                        {/* Inline editovatelný název (vzor hlavičky receptury) */}
                                        <input
                                            value={dodEditData?.nazev || ''}
                                            onChange={e => setDodEditData({ ...dodEditData, nazev: e.target.value })}
                                            placeholder={dodDetail === 'new' ? 'Název dodavatele…' : ''}
                                            className="w-full"
                                            style={{
                                                background: 'none', border: 'none', outline: 'none', padding: 0,
                                                fontSize: 17, fontWeight: 700, color: 'var(--text-primary)',
                                                borderBottom: '1px dashed transparent',
                                            }}
                                            onFocus={e => e.target.style.borderBottomColor = SECTION_COLOR}
                                            onBlur={e => e.target.style.borderBottomColor = 'transparent'}
                                        />

                                        {/* Identifikátor — IČO je hlavní údaj, proto hned pod názvem */}
                                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                                            {dodEditData?.je_zahranicni ? (
                                                <>
                                                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                                                        style={{ background: 'rgba(139,92,246,0.14)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.35)' }}>
                                                        <Globe size={12} /> Zahraniční — bez českého IČO
                                                    </span>
                                                    <input value={dodEditData.dic || ''} onChange={e => setDodEditData({ ...dodEditData, dic: e.target.value })}
                                                        placeholder="VAT / daňové číslo"
                                                        style={{ ...inputStyle, width: 190, fontSize: 12, fontFamily: 'monospace', padding: '4px 8px' }} />
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>IČO</span>
                                                    <input value={dodEditData?.ico || ''} onChange={e => setDodEditData({ ...dodEditData, ico: e.target.value })}
                                                        placeholder="8 číslic"
                                                        style={{
                                                            ...inputStyle, width: 130, fontSize: 13, fontWeight: 700, fontFamily: 'monospace', padding: '4px 8px',
                                                            borderColor: dodEditData?.ico ? undefined : 'rgba(239,68,68,0.5)',
                                                        }} />
                                                    <button onClick={aresRefresh} disabled={aresRefreshing || dodDetail === 'new' || !dodEditData?.ico}
                                                        title={dodDetail === 'new' ? 'Nejdřív dodavatele ulož' : 'Přetáhnout aktuální údaje z ARES podle IČO'}
                                                        className="flex items-center gap-1.5 rounded-lg text-[11px] font-semibold"
                                                        style={{
                                                            background: 'rgba(245,158,11,0.12)', color: SECTION_COLOR,
                                                            border: `1px solid ${SECTION_COLOR}40`, padding: '4px 10px',
                                                            cursor: (aresRefreshing || dodDetail === 'new' || !dodEditData?.ico) ? 'not-allowed' : 'pointer',
                                                            opacity: (dodDetail === 'new' || !dodEditData?.ico) ? 0.5 : 1,
                                                        }}>
                                                        {aresRefreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} ARES
                                                    </button>
                                                    {!dodEditData?.ico && (
                                                        <span className="text-[10px]" style={{ color: '#EF4444' }}>chybí — bez něj hrozí dvojí karta</span>
                                                    )}
                                                </>
                                            )}
                                            <button onClick={() => setDodEditData(p => ({ ...p, je_zahranicni: !p.je_zahranicni, ...(p.je_zahranicni ? {} : { ico: '' }) }))}
                                                className="text-[10px] font-medium"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>
                                                {dodEditData?.je_zahranicni ? 'má české IČO' : 'zahraniční dodavatel'}
                                            </button>
                                        </div>

                                        {/* Štítky — přepínají se klikem hned pod názvem */}
                                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                            {dodKategorie.map(k => {
                                                const vybrany = (dodEditData?._kategorie || []).includes(k.kod);
                                                return (
                                                    <button key={k.kod} type="button"
                                                        onClick={() => setDodEditData(p => ({
                                                            ...p,
                                                            _kategorie: vybrany
                                                                ? (p._kategorie || []).filter(x => x !== k.kod)
                                                                : [...(p._kategorie || []), k.kod],
                                                        }))}
                                                        className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                                                        style={{
                                                            background: vybrany ? STITEK_BARVY[k.kod] || '#94A3B8' : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                                                            color: vybrany ? '#fff' : mutedColor,
                                                            border: `1px solid ${vybrany ? (STITEK_BARVY[k.kod] || '#94A3B8') : 'transparent'}`,
                                                            cursor: 'pointer',
                                                        }}>
                                                        {k.nazev}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className={isMobile ? 'flex items-center gap-2 flex-wrap' : 'flex items-center gap-2'} style={{ flexShrink: 0 }}>
                                        {/* Přepínač aktivní — stejný jako u receptur */}
                                        {dodEditData && (
                                            <button onClick={() => setDodEditData(p => ({ ...p, aktivni: !(p.aktivni ?? true) }))}
                                                title={(dodEditData.aktivni ?? true) ? 'Aktivní — klikni pro deaktivaci' : 'Neaktivní'}
                                                className="flex items-center gap-1.5" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>
                                                <span style={{ width: 34, height: 20, borderRadius: 999, flexShrink: 0, position: 'relative', transition: 'background 0.2s', background: (dodEditData.aktivni ?? true) ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)') }}>
                                                    <span style={{ position: 'absolute', top: 2, left: (dodEditData.aktivni ?? true) ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                                                </span>
                                            </button>
                                        )}
                                        {dodDetail !== 'new' && (
                                            <button onClick={() => delDod(dodDetail)} style={{ ...btnSecondary, padding: '6px 10px', color: '#EF4444' }}>
                                                <Trash2 size={13} /> Smazat
                                            </button>
                                        )}
                                        <button onClick={saveDod} style={{ ...btnPrimary, padding: '6px 14px' }} disabled={dodSaving}>
                                            {dodSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit změny
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Hero — štítky a klíčová čísla. Na širokém displeji je místo něj
                                stálá karta vpravo, aby se čísla neopakovala dvakrát. */}
                            {dodDetail !== 'new' && dodDetailData && !isWide && (
                                <DodavatelHero dodavatel={dodDetailData} stats={dodStats}
                                    isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} isMobile={isMobile} />
                            )}

                            {/* Tabs */}
                            <div className="flex gap-1 px-4 pt-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                {[{ id: 'info', label: 'Základní info' }, { id: 'kalendar', label: 'Kalendář závozů' }, { id: 'sortiment', label: 'Sortiment' }].map(st => (
                                    <button key={st.id} onClick={() => setDodDetailTab(st.id)}
                                        className="px-3 py-2 text-xs font-medium"
                                        style={{ color: dodDetailTab === st.id ? SECTION_COLOR : mutedColor, background: 'none', border: 'none',
                                            borderBottom: `2px solid ${dodDetailTab === st.id ? SECTION_COLOR : 'transparent'}`, cursor: 'pointer', marginBottom: -1 }}>
                                        {st.label}
                                    </button>
                                ))}
                            </div>

                            <div className="p-4 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
                                {/* ─ INFO TAB ─ */}
                                {dodDetailTab === 'info' && dodEditData && (
                                    <div className="space-y-3">

                                        {/* ── Kontakt ── */}
                                        <DodSekce nazev="Kontakt" isDark={isDark} secondaryColor={secondaryColor}>
                                        <div className="grid grid-cols-2 gap-4">
                                            {renderFormField('Kontaktní osoba', <input value={dodEditData.kontaktni_osoba || ''} onChange={e => setDodEditData({ ...dodEditData, kontaktni_osoba: e.target.value })} style={inputStyle} />)}
                                            {renderFormField('Telefon', <input value={dodEditData.telefon || ''} onChange={e => setDodEditData({ ...dodEditData, telefon: e.target.value })} style={inputStyle} />)}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mt-3">
                                            {renderFormField('Email', <input type="email" value={dodEditData.email || ''} onChange={e => setDodEditData({ ...dodEditData, email: e.target.value })} style={inputStyle} />)}
                                            {renderFormField('Adresa', <input value={dodEditData.adresa || ''} onChange={e => setDodEditData({ ...dodEditData, adresa: e.target.value })} style={inputStyle} />)}
                                        </div>
                                        </DodSekce>

                                        {/* ── Objednávání a fakturace ──
                                            Nejen jak se objednává, ale i co se s fakturou
                                            děje potom: čím se platí a kudy doklad přijde
                                            do systému. Obojí se dosud vědělo jen z hlavy
                                            nebo z minulých dokladů. */}
                                        <DodSekce nazev="Objednávání a fakturace" isDark={isDark} secondaryColor={secondaryColor}
                                            doplnek={dodDetail !== 'new' && dodDetailData?.dny?.some(x => x.objednavka || x.zavoz) ? (
                                                <div className="flex items-center gap-3 text-[10px]">
                                                    <span style={{ color: mutedColor }}>
                                                        obj.: <b style={{ color: '#3B82F6' }}>{dodDetailData.dny.filter(x => x.objednavka).map(x => DNY_LABELS[x.den]).join(', ') || '—'}</b>
                                                    </span>
                                                    <span style={{ color: mutedColor }}>
                                                        závoz: <b style={{ color: '#22C55E' }}>{dodDetailData.dny.filter(x => x.zavoz).map(x => DNY_LABELS[x.den]).join(', ') || '—'}</b>
                                                    </span>
                                                </div>
                                            ) : null}>
                                        <div className="grid grid-cols-2 gap-4">
                                            {renderFormField('Dodací lhůta (prac. dní)', <input type="number" min="0" max="30" value={dodEditData.dodaci_lhuta_dni || ''} onChange={e => setDodEditData({ ...dodEditData, dodaci_lhuta_dni: e.target.value ? parseInt(e.target.value) : null })} style={inputStyle} placeholder="např. 2" />)}
                                            {renderFormField('Způsob objednávky', <select value={dodEditData.zpusob_objednavky || ''} onChange={e => setDodEditData({ ...dodEditData, zpusob_objednavky: e.target.value || null })} style={selectStyle}>
                                                <option value="" style={optionStyle}>—</option>
                                                <option value="eshop" style={optionStyle}>E-shop</option>
                                                <option value="telefon" style={optionStyle}>Telefon</option>
                                                <option value="email" style={optionStyle}>E-mail</option>
                                                <option value="sms" style={optionStyle}>SMS</option>
                                                <option value="whatsapp" style={optionStyle}>WhatsApp</option>
                                                <option value="osobne" style={optionStyle}>Osobně</option>
                                            </select>)}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mt-3">
                                            {renderFormField('Způsob platby', <select value={dodEditData.zpusob_platby || ''} onChange={e => setDodEditData({ ...dodEditData, zpusob_platby: e.target.value || null })} style={selectStyle}>
                                                <option value="" style={optionStyle}>—</option>
                                                {Object.entries(ZPUSOB_PLATBY_LABEL).map(([v, l]) => (
                                                    <option key={v} value={v} style={optionStyle}>{l}</option>
                                                ))}
                                            </select>, 'Předvyplní se u nového dokladu od tohoto dodavatele.')}
                                            {renderFormField('Jak chodí faktura do systému', <select value={dodEditData.zpusob_prichodu_faktury || ''} onChange={e => setDodEditData({ ...dodEditData, zpusob_prichodu_faktury: e.target.value || null })} style={selectStyle}>
                                                <option value="" style={optionStyle}>—</option>
                                                {Object.entries(PRICHOD_FAKTURY).map(([v, m]) => (
                                                    <option key={v} value={v} style={optionStyle}>{m.label}</option>
                                                ))}
                                            </select>, PRICHOD_FAKTURY[dodEditData.zpusob_prichodu_faktury]?.popis)}
                                        </div>
                                        </DodSekce>

                                        {/* ── Interní ── */}
                                        <DodSekce nazev="Interní" isDark={isDark} secondaryColor={secondaryColor}>
                                        {renderFormField('Poznámka', <textarea value={dodEditData.poznamka || ''} onChange={e => setDodEditData({ ...dodEditData, poznamka: e.target.value })} rows={2} style={inputStyle} />)}
                                        <div className="mt-3">
                                        {renderFormField('Pokyny pro AI čtení dokladů', <textarea value={dodEditData.scan_pokyny || ''} onChange={e => setDodEditData({ ...dodEditData, scan_pokyny: e.target.value })} rows={3} style={inputStyle} placeholder={'Specialitky tohoto dodavatele pro AI (např. množství ber ze sloupce „Počet kusů“, ne „Počet kartonů“; zálohy sudů 0 % mimo DPH rozpad).'} />)}
                                        <label className="flex items-center gap-2 text-[12px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                                            <input type="checkbox" checked={!!dodEditData.scan_jen_souhrn} onChange={e => setDodEditData({ ...dodEditData, scan_jen_souhrn: e.target.checked })} />
                                            Doklady brát jen souhrnem (řádek na sazbu DPH z rekapitulace, bez položek)
                                        </label>
                                        </div>
                                        <label className="flex items-center gap-2 text-sm mt-3" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={dodEditData.aktivni ?? true} onChange={e => setDodEditData({ ...dodEditData, aktivni: e.target.checked })} /> Aktivní
                                        </label>
                                        </DodSekce>
                                        {dodError && <p className="text-xs" style={{ color: '#EF4444' }}>{dodError}</p>}

                                    </div>
                                )}

                                {/* ─ SORTIMENT TAB ─ */}
                                {dodDetailTab === 'sortiment' && (
                                    dodDetail === 'new' ? (
                                        <p className="text-xs py-6 text-center" style={{ color: mutedColor }}>
                                            Sortiment se ukáže, až bude dodavatel uložený.
                                        </p>
                                    ) : dodSortimentLoading ? (
                                        <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" style={{ color: mutedColor }} /></div>
                                    ) : (
                                        <SortimentSeznam data={dodSortiment} isDark={isDark}
                                            mutedColor={mutedColor} secondaryColor={secondaryColor}
                                            onOtevrit={otevritSortiment} />
                                    )
                                )}

                                {/* ─ KALENDÁŘ TAB ─ */}
                                {dodDetailTab === 'kalendar' && dodEditData && (
                                    <div>
                                        {dodDetail === 'new' ? (
                                            <p className="text-xs py-4" style={{ color: mutedColor }}>Nejdříve uložte dodavatele.</p>
                                        ) : (
                                            <DodavatelKalendar dodForm={dodEditData} setDodForm={setDodEditData} inputStyle={inputStyle} secondaryColor={secondaryColor} mutedColor={mutedColor} isDark={isDark} />
                                        )}
                                    </div>
                                )}

                            </div>
                        </div>
                )}
                panel={() => (dodDetail && dodDetail !== 'new') ? (
                    <DodavatelStatistiky
                        data={dodStats} loading={dodStatsLoading} chyba={dodStatsError}
                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                        cardStyle={cardStyle}
                        onOtevritSortiment={otevritSortiment}
                    />
                ) : null}
            />
        </div>
    );
}

/**
 * Co od dodavatele bereme — položky z jeho platných ceníků.
 *
 * Panel statistik umí jen počty („18 surovin"); na otázku „vede tenhle
 * dodavatel mouku?" odpoví až jména. Cena je poslední platná, historie patří
 * do Špižírny — proto je každá položka zároveň prokliknutí tam.
 */
function SortimentSeznam({ data, isDark, mutedColor, secondaryColor, onOtevrit }) {
    const skupiny = [
        { klic: 'suroviny',  nazev: 'Suroviny',        typ: 'MAT',  Ikona: Package },
        { klic: 'obaly',     nazev: 'Obaly',           typ: 'OBA',  Ikona: Package },
        { klic: 'spotrebni', nazev: 'Spotřební zboží', typ: 'SPOZ', Ikona: Package },
    ].filter(sk => (data?.[sk.klic] || []).length > 0);

    if (!data || skupiny.length === 0) {
        return (
            <p className="text-xs py-8 text-center" style={{ color: mutedColor }}>
                U tohoto dodavatele nemáme v ceníku žádnou položku.
            </p>
        );
    }

    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    return (
        <div className="space-y-4">
            {skupiny.map(sk => (
                <div key={sk.klic}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>
                            {sk.nazev} <span style={{ color: mutedColor }}>{data[sk.klic].length}</span>
                        </span>
                        {onOtevrit && (
                            <button onClick={() => onOtevrit(sk.typ)}
                                className="text-[11px] font-semibold"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>
                                Otevřít ve Špižírně
                            </button>
                        )}
                    </div>
                    <div style={{ borderRadius: 10, border: `1px solid ${hranice}`, overflow: 'hidden' }}>
                        {data[sk.klic].map((x, i) => (
                            <div key={`${sk.klic}-${x.id}`} className="flex items-center gap-3"
                                style={{ padding: '9px 12px', borderTop: i === 0 ? 'none' : `1px solid ${hranice}` }}>
                                <span className="text-[10px] font-mono shrink-0" style={{ color: mutedColor, width: 74 }}>{x.kod}</span>
                                <span className="text-[13px] truncate flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>{x.nazev}</span>
                                {x.dodavatelsky_kod && (
                                    <span className="text-[10px] font-mono shrink-0" style={{ color: mutedColor }}>{x.dodavatelsky_kod}</span>
                                )}
                                {x.je_primarni && (
                                    <span className="text-[9.5px] font-semibold shrink-0" style={{ color: SECTION_COLOR }}>primární</span>
                                )}
                                <span className="text-[13px] font-semibold tabular-nums shrink-0" style={{ color: 'var(--text-primary)' }}>
                                    {x.cena == null ? '—' : `${x.cena.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} Kč`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Kalendář dodávek/objednávek ──────────────────────────
const DNY_LABELS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

function DodavatelKalendar({ dodForm, setDodForm, inputStyle, secondaryColor, mutedColor, isDark }) {
    const dny = dodForm._dny || [0,1,2,3,4,5,6].map(den => ({ den, objednavka: false, deadline: '', zavoz: false }));

    const update = (den, field, value) => {
        const updated = dny.map(d => d.den === den ? { ...d, [field]: value } : d);
        setDodForm({ ...dodForm, _dny: updated });
    };

    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    return (
        <div style={{ paddingTop: 4 }}>
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: secondaryColor }}>Kalendář objednávek a závozů</p>

            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: mutedColor, width: 100 }}>Den</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#3B82F6', width: 90 }}>Objednávka</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: mutedColor, width: 110 }}>Deadline</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#22C55E', width: 90 }}>Závoz</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dny.map(d => {
                            const hasObj = d.objednavka;
                            const hasZav = d.zavoz;
                            const isWeekend = d.den >= 5;
                            const rowBg = hasObj || hasZav
                                ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.015)')
                                : 'transparent';
                            return (
                                <tr key={d.den} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}`, background: rowBg }}>
                                    <td style={{ padding: '10px 12px' }}>
                                        <span className="text-xs font-bold" style={{ color: isWeekend ? mutedColor : 'var(--text-primary)' }}>
                                            {['Pondělí','Úterý','Středa','Čtvrtek','Pátek','Sobota','Neděle'][d.den]}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                        <label className="flex items-center justify-center cursor-pointer">
                                            <input type="checkbox" checked={d.objednavka} onChange={e => update(d.den, 'objednavka', e.target.checked)}
                                                style={{ accentColor: '#3B82F6', width: 16, height: 16 }} />
                                        </label>
                                    </td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                        {d.objednavka ? (
                                            <input type="text" inputMode="numeric" placeholder="HH:MM" maxLength={5}
                                                value={d.deadline || ''}
                                                onChange={e => {
                                                    let v = e.target.value.replace(/[^0-9:]/g, '');
                                                    if (v.length === 2 && !v.includes(':') && d.deadline?.length < 3) v += ':';
                                                    update(d.den, 'deadline', v.slice(0, 5));
                                                }}
                                                style={{ ...inputStyle, fontSize: 13, padding: '5px 8px', textAlign: 'center', width: 80, margin: '0 auto', fontFamily: 'monospace', fontWeight: 600 }} />
                                        ) : (
                                            <span style={{ color: mutedColor, fontSize: 11 }}>—</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                        <label className="flex items-center justify-center cursor-pointer">
                                            <input type="checkbox" checked={d.zavoz} onChange={e => update(d.den, 'zavoz', e.target.checked)}
                                                style={{ accentColor: '#22C55E', width: 16, height: 16 }} />
                                        </label>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="text-[10px] mt-3" style={{ color: mutedColor }}>
                Objednávka = den kdy se objednává (do deadline) · Závoz = den dodání zboží
            </p>
        </div>
    );
}


